import { useCallback } from 'react'

import {
  convertOccupancies,
} from './utils'

import {
  tripLoginModalSignUpDismiss,
  tripLoginModalSignUpView,
} from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { BookmarkDatesAndOccupancy } from 'wishlists/types'
import { useHotelWishlistDisplayFields } from 'wishlists/hooks/useWishlistDisplayFields'
import WishlistOfferBookmarkButton from 'wishlists/components/WishlistOfferBookmarkButton'

interface Props extends Utils.PropsWithDataAttributes {
  offer: App.HotelOffer | App.HotelOfferSummary
  datesAndGuests: BookmarkDatesAndOccupancy
  roomTypeId?: string
  withLabel?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['withLabel']
  size?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['size']
  iconKind?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['iconKind']
  iconVariant?: React.ComponentProps<typeof WishlistOfferBookmarkButton>['iconVariant']
}

function HotelBaseBookmarkButton({
  offer,
  datesAndGuests,
  roomTypeId,
  withLabel,
  size,
  iconKind,
  iconVariant,
  ...rest
}: Props) {
  const wishlistDisplayFields = useHotelWishlistDisplayFields(offer)

  const trackTripLoginModalSignupView = useCallback(() => {
    fireInteractionEvent(
      tripLoginModalSignUpView(roomTypeId === undefined ? 'save' : 'save_room'),
    )
  }, [roomTypeId])
  const trackTripLoginModalSignupDismiss = useCallback(() => {
    fireInteractionEvent(
      tripLoginModalSignUpDismiss(
        roomTypeId === undefined ? 'save' : 'save_room',
      ),
    )
  }, [roomTypeId])

  // We don't support saving flight bundles from the offer page yet, so we hide
  // the save button for now
  if (offer?.bundledWithFlightsOnly) return null

  return (
    <WishlistOfferBookmarkButton
      {...rest}
      item={{
        type: offer.type,
        code: offer.id,
        packageId: offer.packages.find((pkg) => pkg.roomType?.id === roomTypeId)?.id,
        roomTypeId,
        startDate: datesAndGuests.checkInDate?.format(ISO_DATE_FORMAT),
        endDate: datesAndGuests.checkOutDate?.format(ISO_DATE_FORMAT),
        occupancies: convertOccupancies(datesAndGuests.occupancies),
      }}
      {...wishlistDisplayFields}
      withLabel={withLabel}
      size={size}
      iconKind={iconKind}
      iconVariant={iconVariant}
      labelVariant={roomTypeId ? 'room' : 'default'}
      onShowLogin={trackTripLoginModalSignupView}
      onDismissLogin={trackTripLoginModalSignupDismiss}
    />
  )
}

export default HotelBaseBookmarkButton
